import s from "./styles.module.scss";
import CustomDropdown from "../custom-dropdown";
import pencilIcon from "../../assets/pencil.svg";
import colorboxIcon from "../../assets/colorbox.svg";
import { useState, useRef, useEffect } from "react";

const COLOR_PALETTE = [
  { name: 'Tomato', hex: '#d50000' },
  { name: 'Flamingo', hex: '#e67c73' },
  { name: 'Tangerine', hex: '#f4511e' },
  { name: 'Banana', hex: '#f6bf26' },
  { name: 'Sage', hex: '#33b679' },
  { name: 'Basil', hex: '#0b8043' },
  { name: 'Peacock', hex: '#039be5' },
  { name: 'Blueberry', hex: '#3f51b5' },
  { name: 'Lavender', hex: '#7986cb' },
  { name: 'Grape', hex: '#8e24aa' },
  { name: 'Graphite', hex: '#616161' },
];

const CourseItem = ({
  courseData,
  selectedSections,
  courseKey,
  onSectionChange,
  onCourseNameChange,
  selectedColor,
  onColorChange
}) => {
  const [isEditingName, setIsEditingName] = useState(false);
  const [editedName, setEditedName] = useState(courseData.course_name);
  const [isColorPickerOpen, setIsColorPickerOpen] = useState(false);
  const colorPickerRef = useRef(null);

  const currentColor = selectedColor || '#039be5';

  const hexToRgba = (hex, opacity) => {
    const r = parseInt(hex.slice(1, 3), 16);
    const g = parseInt(hex.slice(3, 5), 16);
    const b = parseInt(hex.slice(5, 7), 16);
    return `rgba(${r}, ${g}, ${b}, ${opacity})`;
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (colorPickerRef.current && !colorPickerRef.current.contains(event.target)) {
        setIsColorPickerOpen(false);
      }
    };

    if (isColorPickerOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isColorPickerOpen]);

  const handleCourseNameChange = (e) => {
    setEditedName(e.target.value);
  };

  const handleNameEdit = () => {
    setIsEditingName(true);
  };

  const handleNameSave = () => {
    onCourseNameChange(courseKey, editedName);
    setIsEditingName(false);
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter") {
      handleNameSave();
    }
  };

  const handleSectionChange = (selectedOptionText) => {
    // Find the section object that matches the selected text
    const selectedSection = courseData.sections.find(
      (section) => `${section.name} - ${section.days_of_week || "None"} - ${section.timing || "None"}` === selectedOptionText
    );
    if (selectedSection) {
      onSectionChange(courseKey, selectedSection);
    }
  };

  const handleColorSelect = (color) => {
    if (onColorChange) {
      onColorChange(courseKey, color.hex);
    }
    setIsColorPickerOpen(false);
  };


  // Prepare options for dropdown (section name + days + timing)
  const sectionOptions = courseData.sections.map(
    (section) => `${section.name} - ${section.days_of_week || "None"} - ${section.timing || "None"}`
  );

  // Get current selected value for dropdown
  const currentSelectedValue = selectedSections[courseKey]
    ? `${selectedSections[courseKey].name} - ${selectedSections[courseKey].days_of_week || "None"} - ${selectedSections[courseKey].timing || "None"}`
    : "";

    return (
      <div className={s.courseItem} style={{
        border: `2px solid ${currentColor}`,
        backgroundColor: hexToRgba(currentColor, 0.1)
        }}>
        <div className={s.courseHeader}>
          <div className={s.colorPickerContainer} ref={colorPickerRef}>
            <button 
              className={s.colorButton}
              onClick={() => setIsColorPickerOpen(!isColorPickerOpen)}
              style={{ backgroundColor: currentColor}}
              aria-label="Pick color"
            >
            </button>
            
            {isColorPickerOpen && (
              <div className={s.colorPalette}>
                {COLOR_PALETTE.map((color) => (
                  <button
                    key={color.hex}
                    className={s.colorOption}
                    style={{ backgroundColor: color.hex }}
                    onClick={() => handleColorSelect(color)}
                    title={color.name}
                  >
                    {currentColor === color.hex && (
                      <svg width="20" height="20" viewBox="0 0 16 12" fill="white">
                        <path d="M6 11L2 7l1.5-1.5L6 8l5.5-5.5L13 4z"/>
                      </svg>
                    )}
                  </button>
                ))}
              </div>
            )}
          </div>
          
          <img
            src={pencilIcon}
            alt="Edit"
            className={s.courseIcon}
            onClick={handleNameEdit}
          />
          {isEditingName ? (
            <input
              type="text"
              className={s.courseTitle}
              value={editedName}
              onChange={handleCourseNameChange}
              onBlur={handleNameSave}
              onKeyPress={handleKeyPress}
              autoFocus
            />
          ) : (
            <h3 className={s.courseTitle} onClick={handleNameEdit}>
              {courseData.course_name}
            </h3>
          )}
        </div>
        <CustomDropdown
          options={sectionOptions}
          value={currentSelectedValue}
          onChange={handleSectionChange}
          placeholder="Select a section"
          borderColor={currentColor}  // This passes the selected color
        />
      </div>
    );
  };
  
  export default CourseItem;